import { useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faLockKeyholeOpen, faPeopleGroup } from '@fortawesome/pro-regular-svg-icons';
import { Image, Difficulty, Breadcrumb, Collaborator } from '@tryhackme/thm-ui-components';
import ImagePath from 'src/images/svgs/rooms/retired-rooms-banner.svg';
import { RoomBreadcrumbs } from '../../room.types';
import { RoomBannerActions } from './components/room-banner-actions';
import { StyledLoading } from './components/loading/loading.styles';
import { Loading } from './components';
import { RoomDescription } from './components/room-description';
import { StyledBanner, StyledContainer, StyledContent, StyledTime, StyledWrapperMoreInfo, StyledImage, StyledPaidAccessDaysLeft, StyledCollaborator, StyledBannerContent } from './room-banner.styles';
import { RoomBannerProps, RoomBreadcrumbItemsType } from './room-banner.types';
export const RoomBanner = ({
  roomDetails,
  isLoading,
  isSpecialOrPrivateRoom,
  isBannedRoom,
  specialRoom,
  isPrivateRoom,
  isLockedRoom,
  isRetiredRoom,
  setRoomProgress,
  roomProgress,
  setShowVideo,
  splitScreenTabs,
  splitScreenOpen,
  vmStartType
}: RoomBannerProps) => {
  const isBannedOrLockedRoom = isBannedRoom || isLockedRoom;
  const isPrivateOrRetiredRoom = isPrivateRoom || isRetiredRoom;
  const roomBreadcrumbItem = useMemo(() => {
    let item;
    switch (true) {
      case isPrivateRoom:
        {
          item = 'Private Room';
          break;
        }
      case isLockedRoom:
        {
          item = 'Locked Room';
          break;
        }
      case isRetiredRoom:
        {
          item = 'Retired Room';
          break;
        }
      case isBannedRoom:
        {
          item = 'Banned Room';
          break;
        }
      default:
        {
          item = roomDetails?.title ?? '';
          break;
        }
    }
    return item;
  }, [isBannedRoom, isLockedRoom, isPrivateRoom, isRetiredRoom, roomDetails?.title]);
  const moduleBreadcrumbItem = useCallback((module?: RoomBreadcrumbs['module']): Omit<RoomBreadcrumbItemsType, 'id'> | null => {
    if (module) {
      return {
        label: module.title,
        href: module.moduleURL ? `/module/${module.moduleURL}` : ''
      };
    }
    return null;
  }, []);
  const pathBreadcrumbItem = useCallback((path?: RoomBreadcrumbs['path']): Omit<RoomBreadcrumbItemsType, 'id'> | null => {
    if (path) {
      return {
        label: path.title,
        href: '/paths'
      };
    }
    return null;
  }, []);
  const headerImage = useMemo(() => {
    switch (true) {
      case isBannedOrLockedRoom:
        {
          return 'https://assets.tryhackme.com/img/banners/default_tryhackme.png';
        }
      case isRetiredRoom:
        {
          return ImagePath;
        }
      default:
        {
          return roomDetails?.headerImage;
        }
    }
  }, [isBannedOrLockedRoom, isRetiredRoom, roomDetails?.headerImage]);
  const breadcrumbItems: RoomBreadcrumbItemsType[] = useMemo(() => {
    const breadcrumbList: Omit<RoomBreadcrumbItemsType, 'id'>[] = [];
    const pathBreadcrumb = pathBreadcrumbItem(roomDetails?.breadcrumbs?.path);
    const moduleBreadcrumb = moduleBreadcrumbItem(roomDetails?.breadcrumbs?.module);
    if (pathBreadcrumb) breadcrumbList.push(pathBreadcrumb);
    if (moduleBreadcrumb) breadcrumbList.push(moduleBreadcrumb);
    if (breadcrumbList.length === 0) breadcrumbList.push({
      label: 'Learn',
      href: '/hacktivities'
    });
    breadcrumbList.push({
      label: roomBreadcrumbItem,
      href: '/'
    });
    return breadcrumbList.map((breadcrumb, index) => ({
      ...breadcrumb,
      id: index
    }));
  }, [moduleBreadcrumbItem, pathBreadcrumbItem, roomBreadcrumbItem, roomDetails?.breadcrumbs?.module, roomDetails?.breadcrumbs?.path]);
  const hasCollaborator = roomDetails?.collaborator && roomDetails?.collaborator?.length > 0;
  const collaborator = hasCollaborator && roomDetails?.collaborator && hasCollaborator ? roomDetails?.collaborator[0] : null;
  return <StyledBanner id="room-banner" data-testid="room-banner" data-sentry-element="StyledBanner" data-sentry-component="RoomBanner" data-sentry-source-file="room-banner.tsx">
      <>
        <StyledImage $splitScreenOpen={splitScreenOpen} data-sentry-element="StyledImage" data-sentry-source-file="room-banner.tsx">
          {!isLoading && <img src={headerImage} alt="Room Banner" aria-label="Room Banner" height="600" />}
        </StyledImage>
        <StyledContainer data-sentry-element="StyledContainer" data-sentry-source-file="room-banner.tsx">
          {isLoading ? <StyledLoading width="22.8rem" height="2rem" mobileWidth="14rem" /> : <Breadcrumb breadcrumbItems={breadcrumbItems} isDarkVersion />}
          <StyledBannerContent $isBannedOrLockedRoom={isBannedOrLockedRoom} $splitScreenOpen={splitScreenOpen} $isAddCtaPathOutlineRoomsV2ExperimentEnabled={false} data-sentry-element="StyledBannerContent" data-sentry-source-file="room-banner.tsx" />
          <StyledContent $splitScreenOpen={splitScreenOpen} data-sentry-element="StyledContent" data-sentry-source-file="room-banner.tsx">
            {isLoading ? <Loading /> : <>
                {!isSpecialOrPrivateRoom && <Image src={roomDetails ? roomDetails?.imageURL : 'https://assets.tryhackme.com/img/banners/default_tryhackme.png'} errorSrc="https://assets.tryhackme.com/img/banners/default_tryhackme.png" aria-label="Room Icon" height="9rem" width="9rem" />}

                <div>
                  <RoomDescription roomDetails={roomDetails} isBannedRoom={isBannedRoom} isPrivateRoom={isPrivateRoom} isLockedRoom={isLockedRoom} isRetiredRoom={isRetiredRoom} isSpecialOrPrivateRoom={isSpecialOrPrivateRoom} data-testid="room-description" />

                  {!specialRoom && !isPrivateRoom && <StyledWrapperMoreInfo>
                      <Difficulty difficulty={roomDetails ? roomDetails.difficulty : 'info'} hasIcon isDarkVersion />
                      <StyledTime>
                        <FontAwesomeIcon icon={faClock} />
                        <p>{roomDetails?.timeToComplete} min</p>
                      </StyledTime>
                      {roomDetails?.teams && <StyledTime>
                          <FontAwesomeIcon icon={faPeopleGroup} />
                          <p>Team room</p>
                        </StyledTime>}
                      {hasCollaborator && <StyledCollaborator>
                          <Collaborator collaboratorImageLight={collaborator?.imageLight} collaboratorImageDark={collaborator?.imageDark} collaboratorName={collaborator?.name} isDarkVersion />
                        </StyledCollaborator>}
                      {!!roomDetails?.paidAccess?.daysLeft && <StyledPaidAccessDaysLeft id="network-access-left">
                          <FontAwesomeIcon icon={faLockKeyholeOpen} />
                          <p>{roomDetails?.paidAccess?.daysLeft} days of access left</p>
                        </StyledPaidAccessDaysLeft>}
                    </StyledWrapperMoreInfo>}
                </div>
              </>}
          </StyledContent>
          {!isBannedOrLockedRoom && <RoomBannerActions roomProgress={roomProgress} roomDetails={roomDetails} isLoading={isLoading} isPrivateOrRetiredRoom={isPrivateOrRetiredRoom} isPrivateRoom={isPrivateRoom} isRetiredRoom={isRetiredRoom} setRoomProgress={setRoomProgress} setShowVideo={setShowVideo} splitScreenTabs={splitScreenTabs} vmStartType={vmStartType} />}
        </StyledContainer>
      </>
    </StyledBanner>;
};